import { useCallback } from 'react'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import useRequireLogin from 'Pods/ThreeDStudio/Hooks/useRequireLogin'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setIsImageUploading } from 'Pods/ThreeDStudio/Store/actions/ui'

/**
 * Creates a generator image asset from a remote URL (the "paste image URL +
 * Upload image" flow) and sets it as the input image. Shared by the image
 * uploader's URL field and the AI Assistant's "Apply and continue" action.
 *
 * @param {(id?: number, url?: string) => void} setInputImage - Store setter for the input image
 * @returns {(urlString: string) => Promise<boolean>} Resolves true when the asset was created
 */
export default function useSourceUrlImageUpload(setInputImage) {
  const { postData } = useApiPost()
  // Uploading an image requires an account; prompt a logged-out user to sign up.
  const requireLogin = useRequireLogin()
  const isImageUploading = useThreeDStudioStore(state => state.ui.isImageUploading)

  return useCallback(async (urlString) => {
    if (!requireLogin()) {
      return false
    }

    const trimmedUrl = urlString?.trim()
    if (!trimmedUrl) {
      window.showNotification('Please enter an image URL.', { type: 'error', autohide: true })

      return false
    }

    if (isImageUploading) {
      return false
    }

    try {
      setIsImageUploading(true)

      const response = await new Promise((resolve, reject) => {
        postData({
          path: '/api/internal/generator/assets',
          data: { asset_type: 'image', source_url: trimmedUrl },
          onSuccess: resolve,
          onError: reject,
        })
      })

      const { id } = response.data
      if (!id) {
        throw new Error('Invalid source url upload response')
      }

      setInputImage(id, trimmedUrl)

      return true
    } catch {
      window.showNotification('Upload failed. Please try again.', { type: 'error', autohide: true })

      return false
    } finally {
      setIsImageUploading(false)
    }
  }, [isImageUploading, postData, setInputImage, requireLogin])
}
